import { percentToPixel, pixelToPercent } from '@/helpers/utility';
import * as React from 'react';
import { useRef, useState, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import Draggable from 'react-draggable';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import './image-marker.less';
import { calculateMarkerPosition } from './utils';

const DEFAULT_BUFFER = 12;
const INITIAL_SCALE = 1.0;

const ImageMarker = forwardRef((props, ref) => {
  const {
    src,
    markers,
    onAddMarker,
    markerComponent: MarkerComponent,
    bufferLeft = DEFAULT_BUFFER,
    bufferTop = DEFAULT_BUFFER,
    alt = 'Markers',
    extraClass = '',
    isDisableDragMarker = true,
    onMarkerClicked
  } = props;

  const imageRef = useRef(null);
  const [isDisablePan, setIsDisablePan] = useState(false);
  const [currentScaleValue, setCurrentScaleValue] = useState(INITIAL_SCALE);
  const [isImageCentered, setIsImageCentered] = useState(false);
  const zoomFns = useRef({});
  const imageLoadTimeoutRef = useRef(null);

  useImperativeHandle(ref, () => ({
    zoomIn: () => zoomFns.current.zoomIn && zoomFns.current.zoomIn(),
    zoomOut: () => zoomFns.current.zoomOut && zoomFns.current.zoomOut(),
    resetTransform: () => zoomFns.current.resetTransform && zoomFns.current.resetTransform(),
  }), []);

  const handleImageLoad = () => {
    if (zoomFns.current.centerView) {
      zoomFns.current.centerView(undefined, 0);
    }
    if (imageLoadTimeoutRef.current) {
      clearTimeout(imageLoadTimeoutRef.current);
    }
    imageLoadTimeoutRef.current = setTimeout(() => {
      imageLoadTimeoutRef.current = null;
      setIsImageCentered(true);
    }, 100);
  };

  const handleImageClick = useCallback(
    event => {
      if (!imageRef.current || !onAddMarker) {
        return;
      }
      const imageDimentions = imageRef.current.getBoundingClientRect();

      const [top, left] = calculateMarkerPosition(
        event,
        imageDimentions,
        window.scrollY,
        bufferLeft,
        bufferTop
      );

      onAddMarker({
        top,
        left,
      });
    },
    [onAddMarker, bufferLeft, bufferTop]
  );

  const markerOnClicked = useCallback(
    ({ data }) => {
      if (onMarkerClicked) {
        onMarkerClicked({ data });
      }
    },
    [onMarkerClicked]
  );

  const getItemPosition = useCallback(marker => {
    const { top, left } = marker;
    return {
      top: `${top}%`,
      left: `${left}%`,
    };
  }, []);

  const startDrag = useCallback(() => {
    setIsDisablePan(true);
  }, []);

  const dragAction = (e, data, marker) => {
    const imageDimentions = imageRef.current.getBoundingClientRect();
    const currentMarkerXPx =
      percentToPixel(marker.left, imageDimentions.width) +
      data.x * currentScaleValue;
    const currentMarkerXPercent = pixelToPercent(
      currentMarkerXPx,
      imageDimentions.width
    );
    const currentMarkerYPx =
      percentToPixel(marker.top, imageDimentions.height) +
      data.y * currentScaleValue;
    const currentMarkerYPercent = pixelToPercent(
      currentMarkerYPx,
      imageDimentions.height
    );
    const { device } = marker;
    const { position } = device;
    if (position) {
      marker.device.position.position_x = currentMarkerXPercent / 100;
      marker.device.position.position_y = currentMarkerYPercent / 100;
    } else {
      marker.device.position_x = currentMarkerXPercent / 100;
      marker.device.position_y = currentMarkerYPercent / 100;
    }
  };

  const stopDrag = useCallback(() => {
    setIsDisablePan(false);
  }, []);

  const isImagePortrait = () => {
    if (imageRef.current) {
      return imageRef.current.naturalHeight > imageRef.current.naturalWidth;
    }
    return false;
  };

  // Cleanup function to prevent memory leaks
  useEffect(() => {
    return () => {
      if (imageLoadTimeoutRef.current) {
        clearTimeout(imageLoadTimeoutRef.current);
        imageLoadTimeoutRef.current = null;
      }
    };
  }, []);

  return (
    <TransformWrapper
      wheel={{ step: 0.1, disabled: true }}
      minScale={0.2}
      maxScale={2.0}
      initialScale={INITIAL_SCALE}
      panning={{ disabled: isDisablePan }}
      onTransformed={(ref, event) => {
        setCurrentScaleValue(ref.state.scale);
      }}
    >
      {({ zoomIn, zoomOut, resetTransform, centerView, ...rest }) => {
        // Save zoom functions for ref
        zoomFns.current = { zoomIn, zoomOut, resetTransform, centerView };
        return (
          <React.Fragment>
            {/* Only show native zoom controls if no ref is provided */}
            {!ref && (
              <div className="zoom-controls">
                <button 
                  className="zoom-button zoom-out"
                  onClick={() => zoomOut()}
                  type="button"
                >
                  <img src="/assets/img/zoom-out-icon.svg" alt="Zoom Out" />
                </button>
                <button 
                  className="zoom-button zoom-fit"
                  onClick={() => resetTransform()}
                  type="button"
                >
                  <img src="/assets/img/zoom-fit-icon.svg" alt="Zoom Fit" />
                </button>
                <button 
                  className="zoom-button zoom-in"
                  onClick={() => zoomIn()}
                  type="button"
                >
                  <img src="/assets/img/zoom-in-icon.svg" alt="Zoom In" />
                </button>
              </div>
            )}
            <TransformComponent
              wrapperStyle={{
                marginLeft: 'auto',
                marginRight: 'auto',
                width: '100%',
                height: '100%',
              }}
              contentStyle={{
                marginLeft: 'auto',
                marginRight: 'auto',
                width: '100%',
              }}
            >
              <div className="image-marker">
                <img
                  src={src}
                  alt={alt}
                  onClick={handleImageClick}
                  onLoad={handleImageLoad}
                  className={
                    'image-marker__image ' +
                    extraClass +
                    (isImagePortrait()
                      ? ' image-marker__image--portrait'
                      : ' image-marker__image--landscape')
                  }
                  ref={imageRef}
                  style={{ visibility: isImageCentered ? 'visible' : 'hidden' }}
                />
                {markers.map((marker, itemNumber) => (
                  <Draggable
                    key={`draggable-marker-${itemNumber}`}
                    onStart={startDrag}
                    onDrag={(e, data) => dragAction(e, data, marker)}
                    onStop={stopDrag}
                    disabled={isDisableDragMarker}
                    scale={currentScaleValue}
                  >
                    <div
                      className={`image-marker__marker ${
                        !MarkerComponent && 'image-marker__marker--default'
                      }`}
                      key={itemNumber}
                      style={{ ...getItemPosition(marker), cursor: 'pointer' }}
                      data-testid="marker"
                    >
                      {MarkerComponent ? (
                        <MarkerComponent
                          {...marker}
                          itemNumber={itemNumber}
                          markerOnClicked={markerOnClicked}
                        />
                      ) : (
                        itemNumber + 1
                      )}
                    </div>
                  </Draggable>
                ))}
              </div>
            </TransformComponent>
          </React.Fragment>
        );
      }}
    </TransformWrapper>
  );
});

export default ImageMarker;